// Message shapes shared by server and client.
import type { StarterState } from './starter.ts';
import type { TutorialStage } from './tutorial.ts';
import type { CardPrefs } from './social.ts';
import type { Rolls } from './rolls.ts';
import type { Relic } from './items.ts';
import type { Element } from './elements.ts';
import type { Shrine } from './shrines.ts';
import type { EggDTO } from './eggs.ts';
import type { QuestState } from './quests.ts';
import type { PassState } from './pass.ts';
import type { WeekCatchState } from './weekcatch.ts';
import type { ExchangeEdit } from './exchange.ts';
import type { GuildBrief, GuildOp } from './guilds.ts';
import type { SocialOp } from './social.ts';
import type { Trophy } from './tournament.ts';
import type { BoardId, PodiumDTO } from './leaderboards.ts';
import type { CreatureTrophy } from './provenance.ts';
import type { HeldItem } from './held.ts';
import type { TrailState } from './trail.ts';
import type { BoardRow as LineRow } from './board.ts';
import type { MarketCat, MarketCurrency, MarketQuery, MarketRow } from './market-query.ts';
import type { GemPack } from './gems.ts';
import type { ExchangeDTO } from './exchange.ts';
import type { AwayBudget } from './offline.ts';

export interface CreatureDTO {
  id: number;
  species: string;
  level: number;
  xp: number;
  rolls: Rolls;
  stars: number;
  relic: Relic | null;
  caughtAt: number;
  ground: string;
  bound: boolean;
  listed: boolean;
  /** Made by an admin command (grant or fast-forward): never tradable, left out of economy stats. */
  devGranted?: boolean;
  /** Bought on the market at least once: ascending it doesn't count as a catch for the Bestiary. */
  traded?: boolean;
  /**
   * Refine steps per stat (HP, Attack, Defense, Magic, Resist; shared/refine.ts): +1 base stat each.
   * Absent = none. Kept through ascension, stars and trades; never part of the rating.
   */
  refine?: number[];
  /**
   * Stone training steps per stat (same order; shared/train.ts): +1 base stat each on hunts, bosses and the
   * World Boss only, never in PvP. Absent = none. Kept through ascension, stars and trades.
   */
  train?: number[];
  /**
   * War Medals: the numbers of the Guild Wars in which this creature dealt the most damage
   * (shared/guild-war.ts). Permanent; they travel with the creature through trades.
   */
  medals?: number[];
  /**
   * Rune Tablet applied: the element its Coverage slot casts instead of the line's own (shared/moves.ts withTablet).
   * Absent = no tablet. Same power and cooldown; stays through ascension, stars and trades. Tournament tiers that ban
   * Rune Tablets read this field (shared/items.ts hasRuneTablet).
   */
  tablet?: Element;
  /**
   * Nickname from a Name Tag (shared/nametags.ts). Looks only: never part of stats, grade or value. Absent = none.
   * Cleared when the creature is sold on the market; market snapshots never carry it.
   */
  nickname?: string;
  /**
   * Caught on a hunt while an Aura Charm ran (shared/auracharm.ts): 'paid' (bought with diamonds) or 'free'
   * (given free); 'paid' when both ran. Absent = a plain catch. Set once at the catch and kept through
   * ascension, stars and trades, so a market can filter on it.
   */
  boosted?: 'paid' | 'free';
  /**
   * Provenance (shared/provenance.ts): the trainer name of whoever caught, hatched or bound it. Set once; stays
   * through ascension, stars and trades. Absent only on snapshots taken before provenance.
   */
  catcher?: string;
  /** Tournament Trophies: on the champion's sheet when the event was won. Permanent; travels with trades. */
  trophies?: CreatureTrophy[];
  /** Highest Boss Ladder step won with it on the team. Permanent; travels with trades. */
  ladderBest?: number;
  /** On views of someone else's creature only: the owner's skin for its species (shared/skins.ts). Never stored. */
  skin?: string;
  /** Its held item (shared/held.ts): PvP only. Absent or null = none. Comes off before the creature is listed, released or offered. */
  held?: HeldItem | null;
}

export interface Settings {
  /** Which defeated wilds get a seal. off: none; all; new: species not yet caught; rare: Rare or better species. */
  autoCatch: 'off' | 'all' | 'new' | 'rare';
  /** Seals in the order they are used: the first one that is on and in the bag (shared/automation.ts). */
  seals: { id: string; on: boolean }[];
  /**
   * Write-only shortcut: puts this seal first in `seals` and switches it on.
   * Saves from before the seal list carry it; it is folded into `seals` on load.
   */
  seal?: string;
  /** Drink a potion when the fighter is below potionBelow % HP. */
  autoPotion: boolean;
  /** One of POTION_BELOW_CHOICES (shared/automation.ts). */
  potionBelow: number;
  /** Potions in the order they are used: the first one that is on and in the bag. */
  potions: { id: string; on: boolean }[];
  /**
   * Sell new catches rated below this straight away (0 = keep everything).
   * Never releases an aura, Bloodline III-V (index 2+), or the first catch of a species.
   */
  autoSellBelow: number;
  /**
   * Opt-in auto-move: every 10 minutes and after a level up, move to the best
   * Safe hunt in the current region for XP or coins. Absent or 'off' keeps the
   * hunt the player picked.
   */
  autoMove?: 'off' | 'xp' | 'coins';
  /** Auto-favorite rules (shared/automation.ts autoFavorite): every Aura (absent = on), every Bloodline III+, every catch at or above a grade. */
  favAuras?: boolean;
  favBloodline?: boolean;
  favGrade?: 'off' | 'B' | 'A' | 'S' | 'S+';
  /** Auto-favorite Legendary species. */
  favLegendary?: boolean;
  /** Auto-favorite Talent at or above this percent (0 = off; FAV_TALENTS). */
  favTalent?: number;
  /** Auto-favorite Quality at or above this multiplier (0 = off; FAV_QUALITIES). */
  favQuality?: number;
  /** Show swearing in chat and messages as asterisks (absent = on). Slurs are hidden for everyone regardless. */
  chatFilter?: boolean;
  /** VIP auto-restock (shared/restock.ts). */
  restock?: import('./restock.ts').RestockSettings;
  /** The newest "What's new" entry this account has seen (shared/whatsnew.ts). */
  newsSeen?: string;
  /** Chime when a Golden Wild shows (shared/golden.ts; absent = on). */
  goldenSound?: boolean;
  /** Chime on a catch kept: off (absent), grade A and up, or every one (shared/golden.ts CATCH_SOUNDS). */
  catchSound?: import('./golden.ts').CatchSound;
}

export interface TrainerDTO {
  name: string;
  level: number;
  xp: number;
  coins: number;
  diamonds: number;
  ground: string;
  team: number[];
  /** Rolled XP-share shrines owned (in use or not). */
  shrines?: Shrine[];
  /** Creatures holding an XP Charm. Spare charms sit in the bag as 'xp-charm'. */
  charmed?: number[];
  /** Creatures in `charmed` whose charm was admin-granted; it comes back admin-granted. */
  charmedDev?: number[];
  bag: Record<string, number>;
  relics: Relic[];
  /** Held items not on a creature (shared/held.ts); one on a creature is its CreatureDTO.held. */
  held?: HeldItem[];
  /** The Trail (shared/trail.ts): nodes cleared, each paid once. */
  trail?: TrailState;
  /**
   * Per species: wilds seen, and own catches (an ascension of an own catch counts
   * as catching the next stage). bought: creatures of it bought on the market,
   * or ascended from bought ones; they never count as caught.
   */
  dex: Record<string, { seen: number; caught: number; bought?: number }>;
  settings: Settings;
  /**
   * xpUntil / coinUntil: the free XP and Coin boosts (quests, the pass, the Arena). auraUntil / auraFreeUntil: Aura Charm time
   * bought with diamonds / given free (shared/auracharm.ts). catchUntil: the Catch Charm, earned in ranked seasons
   * (shared/catchcharm.ts). paid: when each boost bought with diamonds ends (shared/boosts.ts).
   */
  boosts: { xpUntil: number; coinUntil: number; vipUntil: number; auraUntil?: number; auraFreeUntil?: number; catchUntil?: number; paid?: Partial<Record<'txp' | 'cxp' | 'loot', number>> };
  daily: { streak: number; last: string };
  /** Bestiary milestones claimed per region (a count into DEX_MILESTONES). */
  dexClaimed?: Record<string, number>;
  /** Daily and weekly quests (shared/quests.ts); made on the first hunt event or log-in of a UTC day or week. */
  quests?: QuestState;
  /** Crafting bench use this week, for recipes with a weekly limit (shared/recipes.ts). */
  crafts?: { week: string; n: Record<string, number> };
  /** Crafts of recipes whose limit spans several weeks (Recipe.everyWeeks), by recipe. */
  craftsLong?: Record<string, { period: string; n: number }>;
  /** Boss wins this week that rolled for the boss legendary (shared/bosses.ts). */
  bossWeek?: { week: string; n: Record<string, number> };
  /** Boss Ladder record (shared/ladder.ts): highest step cleared ever and this UTC month, and wins. */
  ladder?: { best: number; wins: number; month: string; monthBest: number };
  /**
   * Boss Ladder auto-repeat: fights step `step` again every LADDER_REPEAT_MS while `left` committed keys remain,
   * the key is in the bag and the trainer level still opens the step. Only the committed keys are ever spent.
   */
  ladderRun?: LadderRun;
  /** Tournament Tickets (shared/tournament.ts): earned in play, bound to the account, spent on tournament entry. */
  tickets?: number;
  /** Tournament trophies, oldest first. */
  trophies?: Trophy[];
  /** The tournament title shown next to the name in brackets (one the trainer earned). */
  title?: string;
  /** Creature ids moved to the collection: kept safe from releasing and auto-sell. */
  collection?: number[];
  nameColor: string | null;
  /** Season pass (shared/pass.ts): time left, this season's track, and the titles and badges it gave. */
  pass?: PassState;
  /** Weekly Catch (shared/weekcatch.ts): distinct species caught with a seal this UTC week. */
  weekCatch?: WeekCatchState;
  /** Altar offerings made this UTC week (shared/altar.ts). */
  altar?: { week: string; n: number };
  /** Outfit id (shared/outfits.ts); absent means the default. */
  outfit?: string;
  /** Skin tone and hair as a look code (shared/looks.ts); absent until the trainer first picks one, and meaning the default look. */
  look?: string;
  /** Outfits bought in the diamond shop or granted by an admin; earned ones are worked out from level and Bestiary. Never tradable. */
  outfits?: string[];
  /** Outfits in `outfits` that came from an admin command. */
  devOutfits?: string[];
  /** Creature skins bought in the diamond shop (shared/skins.ts). Bound to the account, never tradable. */
  skins?: string[];
  /** Owned skins in use: every creature of the skin's species shows it. */
  skinsOn?: string[];
  /** Skin seasons the shop sells now, set by the server (an admin can close one). */
  skinSeasons?: string[];
  /** Lifetime totals. playMs: time with the game open, counted from playSince (when counting began for this trainer). */
  stats: { kills: number; catches: number; auras: number; /** Ascension stones found on hunts. */ stones?: number; /** Of those, the starter's own stone at the starter rate (CONFIG.stones.starterOneIn). */ starterStones?: number; playMs?: number; playSince?: number };
  /** Late Joiner boost bought (shared/latejoiner.ts): its target level, frozen at purchase. Runs while level < target; cleared at it. */
  lateJoiner?: { target: number };
  /** Today's Late Joiner target, set by the server while the boost is on sale (20+ players count toward the median); absent otherwise. */
  lateJoinerOffer?: number;
  /** When the whole team fainted with no Revive or potion in the bag: the hunt waits in Kinhall until the player picks a hunt. */
  stopped?: number;
  /** The starter checklist (shared/starter.ts); only on trainers made after it shipped. */
  starter?: StarterState;
  /** True while a new trainer may still choose their starter (shared/starter.ts STARTER_CHOICES); gone once chosen. */
  starterPick?: boolean;
  /** True while a new trainer still has the stand-in name from sign-up; the tutorial's first step picks theirs (free, once). */
  namePick?: boolean;
  /** True while a new trainer has yet to choose their look (client/lookpick.ts, after the name and before the starter); any look message clears it. */
  lookPick?: boolean;
  /** The first-time tutorial's stage (shared/tutorial.ts); absent once done or skipped, and on older accounts. */
  tutorial?: TutorialStage;
  /** How many of each bag item came from admin commands; that many can never be listed. */
  devItems?: Record<string, number>;
  /** Your guild, refreshed from the guild tables at log-in and whenever it changes; null or absent when in none. */
  guild?: GuildBrief | null;
  /** Elements whose hall this trainer keeps (shared/halls.ts), refreshed from the hall tables at log-in and when a hall changes hands. */
  halls?: string[];
  /** Creatures shown on your profile card (shared/social.ts SOCIAL.showcaseMax). */
  showcase?: number[];
  /** The creature walking beside your trainer icon (boards, chat, profile). Cleared when it leaves you. */
  companion?: number;
  /** What your player card keeps from other players (shared/social.ts CardPrefs). */
  card?: CardPrefs;
  /** Kinhall emotes bought with diamonds or earned in ranked seasons (shared/kinhall.ts EMOTES); the free ones are not listed. */
  emotes?: string[];
  /** Card frames earned in ranked seasons (shared/frames.ts); the pass frame is not listed (it comes with the pass). */
  frames?: string[];
  /** The card frame chosen (shared/frames.ts): absent = the pass frame while the pass runs, 'none', or an owned frame id. */
  frame?: string;
  /** Coin eggs in incubators (shared/eggs.ts). */
  eggs?: EggDTO[];
  /** The second incubator, bought with coins. */
  incubatorBought?: boolean;
  /** Incubators bought with diamonds, 0..3 (shared/eggs.ts INCUBATOR.diamondPrices). */
  incubatorsDiamond?: number;
  /** Coins that came from admin commands or a fast-forward; an egg paid with them hatches admin-flagged. Never more than coins. */
  devCoins?: number;
  /** When the Founder Box was bought (shared/founder.ts); absent if never. */
  founder?: number;
  /** When the Starter Pack was granted (shared/diamond-packs.ts STARTER); absent if never. Bought once per account. */
  starterPack?: number;
  /** No longer used: Starter Pack and season pass coins were bound until 2026-10-20 (Cole: no coin is bound). Old saves may still carry it; nothing reads it. */
  boundCoins?: number;
  /** Playtest speed (shared/playtest.ts), admins only: the multiplier on this account's game clock; absent means ×1. */
  speed?: number;
  /** Set for good once the playtest speed went above ×1: a test account (shared/playtest.ts quarantine). */
  playtest?: boolean;
  /** Away hunting (shared/offline.ts): away time counted on `day` (UTC), and the last session row credited. */
  awayHunt?: AwayBudget;
  /** When Golden Wilds showed in the last hour (shared/golden.ts), for the cap per hour. */
  goldenAt?: number[];
}

export interface WildDTO {
  uid: number;
  species: string;
  level: number;
  aura: boolean;
  visitor?: boolean;
  hp: number;
  maxHp: number;
  x: number;
  y: number;
  /** ms timestamp when the catch window closes, once defeated */
  downUntil?: number;
  throwing?: boolean;
  /** Its one catch attempt is used (thrown or missed). */
  tried?: boolean;
}

export interface FieldDTO {
  ground: string;
  lead: { id: number; x: number; y: number; hp: number; maxHp: number } | null;
  wilds: WildDTO[];
  restingUntil: number;
  teamHp: Record<number, { hp: number; maxHp: number }>;
}

export type GameEvent =
  /** mv: the move id (shared/moves.ts) the blow used. */
  /** splash: a Wave's blow on a wild next to the target (shared/moves.ts WAVE_SPLASH). */
  | { k: 'hit'; by: 'lead' | 'wild'; uid: number; dmg: number; crit: boolean; mult: number; mv: string; splash?: true }
  | { k: 'spawn'; wild: WildDTO }
  /** bench: XP each bench creature got from this kill through a shrine slot or an XP Charm, as [id, xp]. */
  /** xp: the trainer's kill XP; cxp: the lead's, when it differs. */
  | { k: 'down'; uid: number; xp: number; cxp?: number; coins: number; drops: { item: string; qty: number }[]; relic?: Relic; bench?: [number, number][] }
  | { k: 'throw'; uid: number; seal: string }
  | { k: 'catch'; uid: number; creature: CreatureDTO; sold?: number }
  | { k: 'miss'; uid: number }
  | { k: 'flee'; uid: number }
  | { k: 'faint'; id: number }
  | { k: 'swap'; id: number }
  | { k: 'rest'; until: number; xpLost?: number }
  /** The whole team fainted and a Revive got it back up (shared/items.ts REVIVE); `left` in the bag. */
  | { k: 'revive'; left: number }
  | { k: 'restock'; items: { id: string; qty: number }[]; coins: number }
  /** The whole team fainted with no Revive or potion in the bag: the hunt stopped and the team went back to Kinhall (TrainerDTO.stopped). */
  | { k: 'stopped'; xpLost: number }
  /** Repeated wipes moved the trainer to an easier hunt. */
  | { k: 'retreat'; from: string; to: string }
  /** Auto-move switched to a better Safe hunt in the same region. */
  | { k: 'move'; from: string; to: string; by: 'xp' | 'coins' }
  | { k: 'potion'; item: string; id: number }
  | { k: 'levelup'; level: number; coins: number }
  /** A Weekly Catch milestone (shared/weekcatch.ts) was reached and paid. */
  | { k: 'weekcatch'; at: number; coins: number; seal: string; seals: number; text: string }
  /** An incubator finished: the creature is in storage (or the team, if it had room). */
  | { k: 'hatch'; egg: string; creature: CreatureDTO }
  | { k: 'clevel'; id: number; level: number }
  | { k: 'shrine'; shrine: Shrine; from: 'craft' | 'fuse' }
  /** A Golden Wild was clicked in time (shared/golden.ts): what it paid. */
  | { k: 'golden'; species: string; coins: number; xp: number };

/** The best catch of a species so far (score as caught; ascension adds to it later). */
export interface RecordDTO { species: string; name: string; score: number; rolls: Rolls; at: number }

/** Leaderboards (shared/leaderboards.ts): one board per request. Admin and dev-flagged accounts are left out. */
export type { BoardId };
/**
 * One ranked row. name: the trainer (the guild on the guild board); outfit: for the portrait; tag: guild tag.
 * species/aura: the creature behind the value (best catch board). sub: a second number (guild members).
 */
/** xp: the Level board's total trainer XP, shown when a level is hovered or tapped. */
export interface BoardRow { rank: number; name: string; value: number; outfit?: string; tag?: string; species?: string; aura?: boolean; sub?: number; lv?: number; xp?: number; comp?: CompanionRef }
/** A trainer's companion as other players see it beside the trainer icon: species and whether it is an Aura. */
export interface CompanionRef { species: string; aura?: boolean; /** The owner's skin for it (shared/skins.ts). */ skin?: string }
/** at: the boards' last rebuild; next: the next one. you: your row (your guild's on the guild board), or null when unranked. */
/** podium: last month's top places and Crown (shared/leaderboards.ts PODIUM), once a month has been given. */
export interface LeaderboardsDTO { at: number; next: number; id: BoardId; rows: BoardRow[]; you: BoardRow | null; total: number; podium?: PodiumDTO }

/**
 * Sent with hello when the player was not loaded: what the server ledger
 * holds for this account since its last session ended. Hunting stops while
 * nobody is connected, so there is no hunt progress in here.
 */
export interface AwayDTO {
  /** When the last session ended. */
  since: number;
  /** Listings of yours that sold while you were away, coins after the fee. */
  sold: { kind: string; ref: string; qty: number; price: number; fee: number; at: number; species?: string; level?: number; aura?: boolean }[];
  /** Halls you kept that changed hands meanwhile: to the challenger who beat your team, or to the next team (`to` null: left vacant). */
  halls?: { element: string; to: string | null; why: 'challenge' | 'vacancy'; at: number }[];
  /** Guild Wars your guild fought with you on the roster meanwhile. */
  wars?: { no: number; at: number; tag: string; rank: number; of: number; gp: number; bonus: number; qualified: boolean }[];
  /**
   * Away hunting (shared/offline.ts): the team kept hunting `ground` for `ms` of the time away (capped: today's limit
   * cut it short). coins include level-up coins; levels: trainer level before and after; team: creatures that went up
   * a level, [id, from, to].
   */
  hunt?: { ground: string; ms: number; capped: boolean; coins: number; xp: number; levels: [number, number]; team: [number, number, number][] };
}

/** Chat channels. Guild lines go only to that guild's members; Region lines only to trainers hunting in that region. */
export type ChatChannel = 'world' | 'region' | 'trade' | 'guild' | 'help';
export const CHAT_CHANNELS: readonly ChatChannel[] = ['world', 'region', 'trade', 'guild', 'help'];

/**
 * A feed or chat line. A chat showcase carries `creature`, a snapshot of the
 * creature as it was when shown, and `sid`, an id the hover card looks it up by.
 * Chat lines carry their channel (absent on older lines: World). A market
 * listing shared in Trade carries `listing`, plus `creature` and `sid` when it
 * is a creature.
 */
export interface Broadcast {
  ts: number; kind: 'aura' | 'bloodline' | 'grade' | 'legendary' | 'record' | 'chat' | 'system'; text: string; who?: string; species?: string; creature?: CreatureDTO; sid?: number;
  ch?: ChatChannel;
  listing?: SharedListing;
  /** On a big-catch card (kind aura, bloodline or grade): the catch also set a new species record. */
  record?: boolean;
  /** On chat lines: the outfit the speaker wore, for the small portrait beside the name. */
  outfit?: string;
  /** On chat lines: the speaker's companion, beside the portrait. */
  comp?: CompanionRef;
  /** The speaker's guild tag, as it was when they spoke. */
  tag?: string;
  /** The speaker's chat badge (shared/pass.ts BADGE), as it was when they spoke. */
  badge?: string;
  /** Guild channel lines: the guild they belong to. */
  gid?: number;
  /** A paid Loudspeaker line (shared/loudspeaker.ts): a banner for everyone online and a highlighted World line. */
  loud?: boolean;
  /** Region channel lines: the region (shared/grounds.ts) they were said in. */
  rg?: string;
}

/** A listing as shown on its Trade card: what, how many, the price, and the item or relic name for non-creatures. */
export interface SharedListing { id: number; kind: ListingDTO['kind']; ref: string; qty: number; price: number; /** Per unit; 0 on an old lot. */ unit?: number; name: string; /** Icon id for items, relics and shrines. */ icon?: string; /** Priced in gems; absent means coins. */ cur?: 'gems' }

export interface ListingDTO {
  id: number;
  seller: string;
  kind: 'creature' | 'item' | 'relic' | 'shrine' | 'held';
  ref: string;
  qty: number;
  /** Total for the units left. */
  price: number;
  /** Price per unit; 0 on a lot listed before unit prices, which sells whole. */
  unit: number;
  openAt: number;
  expires: number;
  /** When it sold (sold rows; older ones fall back to openAt). */
  soldAt?: number;
  snapshot: CreatureDTO | Relic | Shrine | HeldItem | null;
  mine: boolean;
  requested: boolean;
  /** Sold by the Exchange, the house trader (shared/exchange.ts), not a player. */
  house?: boolean;
  /** Priced in gems (shared/gems.ts); absent means coins. */
  cur?: 'gems';
}

/** One row of a player's gem history (server/gems.ts). */
export interface GemLedgerRow { ts: number; delta: number; kind: string; ref?: string; bal: number }
/** A gem withdrawal request; `who` on the admin queue only. */
/** Admin > Gems: the switch, totals (gems) and the withdrawal queue. */
export interface GemAdmin { on: boolean; /** Stripe keys set. */ stripe: boolean; /** The cash-out switch. */ cashoutOn: boolean; cashOpen: boolean; held: number; earned: number; fees: number; /** Gems spent on diamond packs. */ shop: number; queued: number; queuedN: number; bought: number; paidOut: number; queue: GemWithdrawalDTO[]; /** Cash-outs pay themselves (server CASHOUT_AUTO); off, every one waits for Pay. */ autoPay?: boolean }
export interface GemWithdrawalDTO { id: number; gems: number; /** What it pays, in US cents, after the spread. */ cents: number; status: string; created: number; done?: number; who?: string; /** Admin queue: this seller was paid in gems by a buyer with a disputed card purchase. */ disputed?: boolean; /** Admin queue: why auto-pay holds it for a person's word. */ hold?: string; /** Admin queue: the last failed pay attempt (retried). */ err?: string }
/** The viewer's gem wallet (MarketDTO.gems): bought and earned gems, what can cash out now, the Stripe payout account. */
export interface GemWalletDTO { bal: number; earned: number; cashable: number; payee: 'none' | 'pending' | 'ready'; cashOpen: boolean; packs: GemPack[]; history: GemLedgerRow[]; withdrawals: GemWithdrawalDTO[] }

/** One page of the Market (server World.sendMarket). */
export interface MarketDTO {
  /** The query this page answers, cleaned. */
  q: MarketQuery;
  rows: MarketRow[];
  total: number;
  page: number;
  pages: number;
  counts: Partial<Record<MarketCat, number>>;
  /** Every listing the page, the book and the viewer's own listings refer to. */
  listings: ListingDTO[];
  /** The viewer's open listings (ids into listings). */
  mine: number[];
  /** One item kind's sell orders, cheapest first (when q.item is set). */
  book?: { ref: string; ids: number[] };
  /** The newest sales on the whole market. */
  sold: ListingDTO[];
  /** The viewer's newest sales and purchases; on a purchase `seller` names who sold it. */
  trades: (ListingDTO & { bought: boolean })[];
  now: number;
  exchange?: ExchangeDTO;
  /** The currencies open on the market, coins first. Gems only while the gem switch is on. */
  curs: MarketCurrency[];
  /** The viewer's gem wallet, while gems are on. */
  gems?: GemWalletDTO;
}

export type ClientMsg =
  /** This window plays; the account's other window waits (server/tabs.ts). */
  | { t: 'playHere' }
  /** Kinhall, the home hall (shared/kinhall.ts): arrive, leave, or walk to a spot on its floor (tiles). */
  | { t: 'kinhall'; op: 'enter' | 'leave' }
  | { t: 'kinhall'; op: 'walk'; x: number; y: number }
  | { t: 'kinhall'; op: 'emote'; id: string }
  /** Urd's Well in Kinhall: heal the hunting team (free, while in the hall). */
  | { t: 'kinhallHeal' }
  /** Buy a Kinhall emote for diamonds (shared/kinhall.ts). */
  | { t: 'emoteBuy'; id: string }
  | { t: 'ground'; id: string }
  | { t: 'team'; ids: number[] }
  /** Turn a shrine on or off, and/or set who sits on its posts. */
  | { t: 'shrine'; id: string; active?: boolean; posts?: number[] }
  /** 10 shards -> one shrine with a rolled tier. */
  | { t: 'shrineCraft' }
  /** 5 shrines of one fusable tier -> one of the next tier. */
  | { t: 'shrineFuse'; ids: string[] }
  /** Put a spare XP Charm on a creature (on: true) or take it back. */
  | { t: 'charm'; id: number; on: boolean }
  | { t: 'settings'; settings: Partial<Settings> }
  | { t: 'buy'; item: string; qty: number }
  | { t: 'sell'; item: string; qty: number }
  | { t: 'release'; ids: number[] }
  /** Release these, but only those graded below `grade` and not protected (shared/automation.ts releasableBelow). */
  | { t: 'releaseBelow'; grade: string; ids: number[] }
  /** Offer 1 to 5 storage creatures at the Altar (shared/altar.ts) for a chance at one ascension or star item. */
  | { t: 'altar'; ids: number[] }
  | { t: 'collect'; id: number; on: boolean }
  | { t: 'ascend'; id: number; /** Which payable item to use (shared/items.ts ascendItems); default the creature's own stone. */ item?: string }
  | { t: 'star'; id: number }
  /**
   * Refine stat `stat` (0-4: HP, Attack, Defense, Magic, Resist) by `steps` (1 if absent), paying in `item`:
   * the star item of the creature's region (the default) or of a later region (shared/refine.ts).
   */
  | { t: 'refine'; id: number; stat: number; steps?: number; item?: string }
  /** Stone-train stat `stat` (0-4) by `steps` (1 if absent), paying coins and the stone `item` of one of its elements (shared/train.ts). */
  | { t: 'train'; id: number; stat: number; steps?: number; item?: string }
  | { t: 'equip'; id: number; relic: string | null }
  /** Put a held item (TrainerDTO.held id) on a creature, swapping out any it holds, or take its item off (held null). */
  | { t: 'hold'; id: number; held: string | null }
  /** Crafting bench: ten pieces forge one held item with a fresh quality roll; el: the Spirit Bead's element. */
  | { t: 'heldForge'; item: string; el?: string }
  /**
   * The Trail (shared/trail.ts): fight trainer node `node` now with `ids` in that order (the hunting team when absent),
   * rows: front or back for each (on the board; defaultRows when absent); replies { t: 'trail', fight: TrailFightDTO }.
   */
  | { t: 'trailFight'; node: string; ids?: number[]; rows?: LineRow[] }
  /** Apply a Rune Tablet from the bag: the creature's Coverage slot becomes the tablet's element (shared/items.ts tabletCheck). */
  | { t: 'tablet'; id: number; element: string }
  /** Awaken a creature with its Awakening Stone from the bag (an Aura needs the Aura Awakening Stone; shared/awakening.ts). Irreversible. */
  | { t: 'awaken'; id: number }
  /** Name a creature with a Name Tag from the bag (one tag per naming), or take its nickname off (name null or empty: free). */
  | { t: 'nickname'; id: number; name: string | null }
  | { t: 'scrapRelic'; relic: string }
  /** Scrap many bag relics at once (Bag > Relics > Scrap…); unknown or worn ids are skipped. */
  | { t: 'scrapRelics'; relics: string[] }
  | { t: 'daily' }
  /** Claim every Bestiary milestone reached in a region. */
  | { t: 'dexClaim'; region: string }
  /** Claim every finished daily and weekly quest (including yesterday's and last week's), the daily all-three bonuses and the weekly chest's reached steps. */
  | { t: 'questClaim' }
  /** Season pass: claim today's pass day, redraw one unfinished daily quest, or wear a chat badge. */
  | { t: 'passClaim' }
  | { t: 'questReroll'; i: number }
  /** Wear a card frame (shared/frames.ts): an owned frame id, 'none', or '' for the default (the pass frame while it runs). */
  | { t: 'cardFrame'; id: string }
  /** Use a boost item from the Bag (an Aura or Catch Charm, shared/catchcharm.ts useCharmItem). */
  | { t: 'useCharm'; item: string }
  | { t: 'passBadge'; id: string | null }
  | { t: 'starter'; op: 'claim' | 'skip' }
  /** A new trainer's starter choice: swaps the starter's species (rolls kept). Once per account. */
  | { t: 'starterPick'; species: string }
  /** First-time tutorial: 'hunt' lets the held first hunt start; 'done' ends it (finished or skipped). */
  | { t: 'tutorial'; op: 'hunt' | 'done' }
  /** A new trainer's first name (server/auth.ts nameProblem); only while namePick is set. */
  | { t: 'namePick'; name: string }
  /** Crafting bench: make a recipe `times` times (1 if absent). */
  | { t: 'craft'; recipe: string; times?: number }
  /** price: the diamond price the player saw (the Late Joiner boost): a higher price now refuses, a lower one is charged. */
  | { t: 'shop'; offer: string; price?: number }
  /** Buy a coin egg for a region you have opened; it goes straight into a free incubator. */
  | { t: 'eggBuy'; egg: string; region: string }
  /** Buy the second incubator (coins only). */
  | { t: 'incubatorBuy' }
  /** Buy the next diamond incubator (50, 100, then 150 diamonds). */
  | { t: 'incubatorDiamond' }
  /** Buy an outfit from the diamond shop (it is worn straight away). */
  | { t: 'outfitBuy'; id: string }
  /** Wear an outfit you own. */
  | { t: 'outfitWear'; id: string }
  /** Change your look (a look code, shared/looks.ts), and the outfit with it if one you own is given. Always free. */
  | { t: 'look'; look: string; outfit?: string }
  /** Buy a creature skin (shared/skins.ts); it is turned on straight away. */
  | { t: 'skinBuy'; id: string }
  /** Use a skin you own on every creature of its species, or stop using it. */
  | { t: 'skinUse'; id: string; on: boolean }
  | { t: 'boss'; id: string }
  /** Throw a seal at a defeated wild now (a click on it), whatever Throw at says: one attempt per wild. */
  | { t: 'catch'; uid: number }
  /** Boss Ladder: fight step `step` now; repeat > 1 commits that many keys in all to auto-repeat (shared/ladder.ts). */
  | { t: 'ladder'; step: number; repeat?: number; stopOnLoss?: boolean }
  /** Stop the Boss Ladder auto-repeat; nothing more is spent. */
  | { t: 'ladderStop' }
  /** ch: the channel, World when absent. */
  | { t: 'chat'; text: string; ch?: ChatChannel }
  /** Pay LOUD.cost coins to send one line to everyone online (shared/loudspeaker.ts). */
  | { t: 'loud'; text: string }
  /** Whether the hunt scene is on screen, so other trainers on the hunt are sent (server/wayfarers.ts); or an emote (shared/kinhall.ts EMOTES) shown to them. */
  | { t: 'wf'; on?: boolean; emote?: string }
  /** The tab went to the background (on) or came back: while it is hidden, field messages go only with something to report. */
  | { t: 'hidden'; on: boolean }
  /** The corner window (client/corner/) is open: the hunt is on screen even with the game's tab in the background. */
  | { t: 'corner'; on: boolean }
  /** A click on the Golden Wild with this id (shared/golden.ts). */
  | { t: 'goldenClaim'; id: number }
  /** Post one of your open market listings in Trade as a card; same rate limit as chat. */
  | { t: 'shareListing'; id: number }
  /** Show one of your creatures in chat as a chip; same rate limit as chat. */
  | { t: 'showcase'; id: number }
  /** Browse the Market (shared/market-query.ts); without q the last query again. */
  | { t: 'market'; q?: MarketQuery }
  /** Buy units of one item kind at the best prices, up to the price each and total the buyer saw. */
  | { t: 'buyBest'; item: string; qty: number; maxUnit: number; maxTotal: number; cur?: MarketCurrency }
  /** Gems (shared/gems.ts): set up the Stripe payout account (replies gemPayee with a link), cash out earned gems, cancel a queued cash-out. */
  | { t: 'gemPayee' }
  | { t: 'gemCashout'; gems: number }
  | { t: 'gemCancel'; id: number }
  /** Gems for the Shop: ask for the balance (replies gemBal), or buy a diamond pack with gems at its card price. */
  | { t: 'gemBal' }
  | { t: 'gemDiamonds'; pack: string; price?: number }
  /** Sell items to the Exchange at its bid (shared/exchange.ts). Replies with the market. */
  | { t: 'exchangeSell'; item: string; qty: number }
  /** One key's price table (shared/prices.ts); replies { t: 'prices', data: PriceTableDTO }. key: species, item id, relic stat or shrine tier. */
  | { t: 'prices'; kind: string; key: string; cur?: MarketCurrency }
  /** Ask for the records board; the reply is { t: 'records', list: RecordDTO[] }. */
  | { t: 'records' }
  /**
   * Someone else's creature for the read-only creature view (client/peek.ts), by where it is shown: a species
   * record, a trainer's best catch, or Kinhall's latest Aura. The reply is { t: 'creatureView', key, c, owner }
   * with key `${of}:${species | name | ''}`; c is null when the creature is gone.
   */
  | { t: 'creatureView'; of: 'record' | 'best' | 'legend'; species?: string; name?: string }
  /** Ask for one leaderboard (default level); the reply is { t: 'leaderboards', data: LeaderboardsDTO }. */
  | { t: 'leaderboards'; board?: BoardId }
  /** Guild actions (shared/guilds.ts); with no op, just ask for your guild view. The reply is { t: 'guild', view: GuildView }. */
  | { t: 'guild'; op?: GuildOp }
  /** Browse guilds by name or tag; the reply is { t: 'guilds', list: GuildSummary[] }. */
  | { t: 'guilds'; q?: string }
  /** The Guild War page data; the reply is { t: 'guildWar', data: GuildWarDTO }. */
  | { t: 'guildWar' }
  /** One page of a war's full battle log; the reply is { t: 'guildWarLog', data: WarLogPage }. */
  | { t: 'guildWarLog'; no: number; page: number }
  /** Friends, messages, block and mute, profiles, friendly battles and training (shared/social.ts); with no op, just ask for your Social view ({ t: 'social', data }). */
  | { t: 'social'; op?: SocialOp }
  /** price is per unit (shared/market.ts). Listing more of an item you already list adds to that listing at its price. */
  | { t: 'list'; kind: 'creature' | 'item' | 'relic' | 'shrine' | 'held'; ref: string; qty: number; price: number; cur?: MarketCurrency }
  | { t: 'unlist'; id: number }
  /** qty: units to take (an old lot sells whole); unit: the unit price the buyer saw, refused if it went up. */
  | { t: 'purchase'; id: number; qty?: number; unit?: number }
  // Ranked Arena (shared/arena.ts). Each replies { t: 'arena', data: ArenaDTO } unless noted.
  | { t: 'arena' }
  | { t: 'arenaQueue'; on: boolean }
  /** Save line-up `slot` (0-4; one past the last adds a new one); rows: front or back for each creature (shared/pvpfight.ts). */
  | { t: 'arenaLineup'; slot: number; ids: number[]; name?: string; rows?: LineRow[] }
  /** Fight with line-up `slot`, or -1 for the hunt team. */
  | { t: 'arenaUse'; slot: number }
  | { t: 'arenaDelete'; slot: number }
  /** Dismiss last season's prize note. */
  | { t: 'arenaNotice' }
  /** A match's turn log; replies { t: 'arenaMatch', id, side, result, summary }. */
  | { t: 'arenaMatch'; id: number }
  /** Someone's line-up without its order; replies { t: 'arenaPeek', peek }. */
  | { t: 'arenaPeek'; name: string }
  // Element Halls (shared/halls.ts). Each replies { t: 'halls', data: HallsDTO } unless noted.
  | { t: 'halls' }
  // World Boss (shared/world-boss.ts): the page replies { t: 'wb', data: WbDTO }; an attack { t: 'wbResult', result, data }.
  | { t: 'wb' }
  | { t: 'wbAttack'; ids: number[] }
  /** Register or replace your team at a hall: 1 to 5 of your creatures of its element, no species twice; rows as arenaLineup. */
  | { t: 'hallRegister'; el: string; ids: number[]; rows?: LineRow[] }
  | { t: 'hallWithdraw'; el: string }
  /** Duel the hall's keeper; the result comes as { t: 'hallResult', duel } to both trainers. */
  | { t: 'hallChallenge'; el: string }
  /** A duel's turn log; replies { t: 'hallDuel', id, side, result, summary }. */
  | { t: 'hallDuel'; id: number }
  /** Practice runs of `ids` (in that order) against the hall's keeper on practice seeds; replies { t: 'hallPractice', el, ids, result: PracticeSummary }. */
  | { t: 'hallPractice'; el: string; ids: number[]; rows?: LineRow[] }
  /** Tournaments (server/tournaments.ts): every reply is { t: 'tour', data: TourDTO }, except tourMatch. */
  | { t: 'tour' }
  | { t: 'tourEnter'; tier: string; ids: number[]; pay: 'ticket' | 'coins'; rows?: LineRow[] }
  | { t: 'tourWithdraw'; tier: string }
  | { t: 'tourBan'; tour: string; id: number }
  /** Your order for the next game; rows: front or back for each creature in it (the board), else the sheet's rows. */
  | { t: 'tourOrder'; tour: string; ids: number[]; rows?: LineRow[] }
  | { t: 'tourTitle'; title: string | null }
  /** Any match of an event; replies { t: 'tourMatch', match: MyMatch }. */
  | { t: 'tourMatch'; tour: string; round: number; slot: number }
  /** Partner streams (server/streams.ts), only while they are on: a heartbeat from the open stream dock, and the dock closing. Replies { t: 'streams', ...StreamsDTO }. */
  | { t: 'streamBeat'; login: string }
  | { t: 'streamStop' }
  | { t: 'admin' }
  /** The player report (shared/player-report.ts); replies { t: 'admPlayers', rows }. */
  | { t: 'admPlayers' }
  /** Add (or rename) and remove a partner Twitch channel. */
  | { t: 'admStreamAdd'; login: string; name?: string }
  | { t: 'admStreamDel'; login: string }
  /** The Exchange: the master switch, or one item's settings (shared/exchange.ts ExchangeEdit); remove stops carrying it. */
  | { t: 'admExchange'; on?: boolean; item?: string; set?: ExchangeEdit; remove?: boolean }
  // Admin/dev commands: rejected server-side for non-admins, each written to the admin_log table.
  | { t: 'admSetLevel'; level: number }
  | { t: 'admGrant'; coins?: number; diamonds?: number }
  | { t: 'admGrantItem'; item: string; qty: number }
  | { t: 'admGrantEgg'; egg: string; region: string }
  | { t: 'admGrantOutfit'; id: string }
  /** Open or close a skin season in the shop (owners keep their skins). */
  | { t: 'admSkinWindow'; season: string; open: boolean }
  /** Free Aura Charm time (hours; one charm's length when absent). */
  | { t: 'admGrantAuraCharm'; hours?: number }
  | { t: 'admGrantCreature'; species: string; level: number; bloodline: number; aura: boolean; talent: TalentPreset }
  | { t: 'admTeamLevel'; level: number }
  | { t: 'admHunt'; id: string }
  | { t: 'admHeal' }
  /** clean: a full reset to a brand-new player account (test flag, gems, queued cash-outs, kept outfits and skins, arena and ranked rows all go). */
  | { t: 'admReset'; clean?: boolean }
  | { t: 'admFastForward'; minutes: number }
  /** Playtest speed for your own account, 1..PLAYTEST.max (shared/playtest.ts). */
  | { t: 'admSpeed'; speed: number }
  /** Turn tournaments on or off for everyone (server/tournaments.ts setEnabled). */
  | { t: 'admTournaments'; on: boolean }
  /** Launch day for tournaments (YYYY-MM-DD, UTC), or null to clear it: each tier's first event waits OPEN_AFTER_DAYS from it. */
  | { t: 'admTourLaunch'; day: string | null }
  /** Open or retire the Founder Box in the diamond shop (shared/founder.ts). */
  | { t: 'admFounder'; open: boolean }
  /**
   * Ranked seasons (shared/ranked.ts, server/ranked.ts): turn them on or off for everyone (on: absent leaves it), end
   * the running season now (prizes, Legend titles, soft reset, the next season starts), or set its theme rule.
   */
  | { t: 'admRanked'; on?: boolean; endSeason?: boolean; theme?: string; strength?: boolean }
  /** Turn PvP synergies on or off for everyone (shared/synergy.ts SYNERGY_SWITCH, kv 'synergy-on'). */
  | { t: 'admSynergy'; on: boolean }
  /** Turn the board (two rows) on or off for every PvP mode (shared/pvpfight.ts BOARD_SWITCH, kv 'board-on'). */
  | { t: 'admBoard'; on: boolean }
  /** Season one switches (kv 'held-on', 'trail-on'): held items in PvP, and the Trail in place of the Bosses screen. */
  | { t: 'admHeld'; on: boolean }
  | { t: 'admTrail'; on: boolean }
  /** Gems (shared/gems.ts): the switch, a test deposit to a trainer by name, and the withdrawal queue. */
  | { t: 'admGems'; on: boolean }
  | { t: 'admStoreLive'; on: boolean }
  | { t: 'admGemCashoutOn'; on: boolean }
  | { t: 'admGemCredit'; name: string; gems: number }
  /** Name a trainer who signed up and has not chosen a name yet, found by email; may be a staff name such as admin (shared/reserved-names.ts). */
  | { t: 'admNameNew'; email: string; name: string }
  | { t: 'admGemCashout'; id: number; op: 'pay' | 'refuse' }
  /** A held item for yourself (admin-flagged, never tradable) at this quality; or remove one of yours by id (taken off its creature first). */
  | { t: 'admGrantHeld'; item: string; quality: number; el?: string }
  | { t: 'admRemoveHeld'; id: string }
  /** Take held-item pieces (bag items `<item>-piece`) out of the admin's own bag. */
  | { t: 'admRemovePieces'; item: string; qty: number }
  /** Clear your own Trail record (first-clear rewards pay again). */
  | { t: 'admTrailReset' }
  /** Schedule a server event (shared/events.ts): times in UTC ms, region id or absent for every hunt. */
  | { t: 'admEvent'; kind: string; mult: number; start: number; end: number; region?: string }
  | { t: 'admEventDel'; id: number };

export type TalentPreset = 'random' | 'average' | 'perfect';

/** What an admin fast-forward did, sent as { t: 'adminFF', ff }. */
export interface FastForwardSummary {
  minutes: number;
  wallMs: number;
  kills: number;
  xp: number;
  xpLost: number;
  coins: number;
  catches: number;
  auras: number;
  wipes: number;
  retreats: number;
  levelFrom: number;
  levelTo: number;
  ground: string;
}

export interface BossResult {
  boss: string;
  win: boolean;
  /** mv: the move id of the blow (shared/moves.ts); ms: when it landed, ms into the fight (absent in older results). */
  log: { by: 'team' | 'boss'; id: number; dmg: number; crit: boolean; hp: number; mv?: string; ms?: number }[];
  /** The team's creature ids in the order they fought (the boss hits the first one standing). */
  ids?: number[];
  bossMaxHp: number;
  teamMaxHp: Record<number, number>;
  rewards: { coins: number; items: { item: string; qty: number }[]; relic?: Relic; creature?: CreatureDTO };
  /** The win rolled for the legendary (false once this week's rolls against this boss are used). */
  legendaryRoll?: boolean;
  /** Legendary rolls left against this boss this week, after this fight. */
  rollsLeft?: number;
  /** This win was the boss's first clear on the Trail (shared/trail.ts): its node, the whole held item and the diamonds. */
  firstClear?: { node: string; item: HeldItem; diamonds: number };
}

export interface LadderRun {
  step: number;
  /** Committed keys not spent yet. */
  left: number;
  stopOnLoss: boolean;
  /** When the next fight starts (ms). */
  next: number;
  fought: number;
  won: number;
}

/** A Boss Ladder fight, sent as { t: 'sync', ladder }. */
export interface LadderResult extends BossResult {
  step: number;
  level: number;
  /** The auto-repeat after this fight; absent when none runs. stopped: why it ended, when it just did. */
  run?: LadderRun;
  stopped?: string;
}
